import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AppLayout from './components/AppLayout';
import Login from './features/auth/Login';
import { WebSocketProvider } from './contexts/WebSocketContext';
import MerchantLogin from './pages/merchant/MerchantLogin';
import MerchantLayout from './pages/merchant/MerchantLayout';
import MerchantDashboard from './pages/merchant/MerchantDashboard';
import MerchantOrders from './pages/merchant/MerchantOrders';
import MerchantSettlements from './pages/merchant/MerchantSettlements';
import MerchantSettings from './pages/merchant/MerchantSettings';
import MerchantWithdraw from './pages/merchant/MerchantWithdraw';
import MerchantAccount from './pages/merchant/MerchantAccount';

// 导入页面组件
import Dashboard from './pages/Dashboard';
import UserList from './pages/users/UserList';
import UserLevels from './pages/users/UserLevels';
import UserVerification from './pages/users/UserVerification';
import KycPaymentManagement from './pages/users/KycPaymentManagement';
import SmsRecordList from './pages/sms/SmsRecordList';
import EmailRecordList from './pages/email/EmailRecordList';
import RechargeManagement from './pages/finance/RechargeManagement';
import WithdrawManagement from './pages/finance/WithdrawManagement';
import TransactionRecords from './pages/finance/TransactionRecords';
import FinanceStatistics from './pages/finance/FinanceStatistics';
import PaymentAccounts from './pages/finance/PaymentAccounts';
import WithdrawMethods from './pages/finance/WithdrawMethods';
import AgentManagement from './pages/team/AgentManagement';
import TeamPerformance from './pages/team/TeamPerformance';
import CommissionManagement from './pages/team/CommissionManagement';
import CommissionLevelConfig from './pages/team/CommissionLevelConfig';
import AdminMessageList from './pages/messages/MessageList';
import MessageSend from './pages/messages/MessageSend';
import VerificationCodeList from './pages/verificationCodes/VerificationCodeList';
import AppMessageList from './pages/app/MessageList';
// 后台管理员管理页面
import AdminList from './pages/admins/AdminList';
// 菜单管理页面
import Menus from './pages/system/Menus';
// 公告管理页面
import NoticeList from './pages/notices/NoticeList';
import NoticeForm from './pages/notices/NoticeForm';
// 资讯管理页面
import NewsList from './pages/news/NewsList';
import NewsForm from './pages/news/NewsForm';
// 帮助手册管理页面
import HelpManualList from './pages/helpManuals/HelpManualList';
import HelpManualForm from './pages/helpManuals/HelpManualForm';

// 首页功能按钮管理
import HomeFunctionButtons from './pages/home/HomeFunctionButtons';

// 定时任务管理
import CronTaskLogs from './pages/cron/CronTaskLogs';

// 第三方平台管理
import ThirdPartyApps from './pages/third-party/ThirdPartyApps';
import ThirdPartyOrders from './pages/third-party/ThirdPartyOrders';
import ThirdPartySettlements from './pages/third-party/ThirdPartySettlements';
import ThirdPartyWithdrawals from './pages/third-party/ThirdPartyWithdrawals';

// 弹窗管理
import PopupList from './pages/popups/PopupList';
import PopupForm from './pages/popups/PopupForm';

// 系统设置
import RegionManagement from './pages/settings/RegionManagement';
import SystemConfig from './pages/settings/SystemConfig';
import EmailConfig from './pages/settings/EmailConfig';
import EmailTest from './pages/settings/EmailTest';
import GmailConfig from './pages/settings/GmailConfig';
import CustomerServiceConfig from './pages/settings/CustomerServiceConfig';
// 个人资料
import Profile from './pages/profile/Profile';

// 受保护的路由组件
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// 公开路由组件
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <Routes>
      {/* 公开路由 */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* 商户登录 */}
      <Route
        path="/merchant/login"
        element={<MerchantLogin />}
      />

      {/* 商户后台 */}
      <Route
        path="/merchant"
        element={<MerchantLayout />}
      >
        <Route index element={<MerchantDashboard />} />
        <Route path="account" element={<MerchantAccount />} />
        <Route path="orders" element={<MerchantOrders />} />
        <Route path="settlements" element={<MerchantSettlements />} />
        <Route path="withdraw" element={<MerchantWithdraw />} />
        <Route path="settings" element={<MerchantSettings />} />
      </Route>

      {/* 受保护的路由 */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <WebSocketProvider>
              <AppLayout>
              <Routes>
                {/* 默认重定向到看板 */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                
                {/* 综合看板 */}
                <Route path="/dashboard" element={<Dashboard />} />
                
                {/* 用户管理 */}
                <Route path="/users/list" element={<UserList />} />
                <Route path="/users/levels" element={<UserLevels />} />
                <Route path="/users/verification" element={<UserVerification />} />
                <Route path="/users/kyc-payment" element={<KycPaymentManagement />} />
                <Route path="/users/verification-codes" element={<VerificationCodeList />} />
                
                {/* 财务管理 */}
                <Route path="/finance/recharge" element={<RechargeManagement />} />
                <Route path="/finance/withdraw" element={<WithdrawManagement />} />
                <Route path="/finance/payment-accounts" element={<PaymentAccounts />} />
                <Route path="/finance/withdraw-methods" element={<WithdrawMethods />} />
                <Route path="/finance/transactions" element={<TransactionRecords />} />
                <Route path="/finance/statistics" element={<FinanceStatistics />} />
                
                {/* 团队管理 */}
                <Route path="/team/agents" element={<AgentManagement />} />
                <Route path="/team/performance" element={<TeamPerformance />} />
                <Route path="/team/commissions" element={<CommissionManagement />} />
                <Route path="/team/commission-config" element={<CommissionLevelConfig />} />
                
                {/* 消息中心 */}
                <Route path="/messages/list" element={<AdminMessageList />} />
                <Route path="/messages/send" element={<MessageSend />} />
                
                {/* 短信记录管理 */}
                <Route path="/sms/records" element={<SmsRecordList />} />

                {/* 邮件记录管理 */}
                <Route path="/email/records" element={<EmailRecordList />} />
                
                {/* 首页管理 */}
                <Route path="/home/function-buttons" element={<HomeFunctionButtons />} />

                <Route path="/app/messages" element={<AppMessageList />} />
                
                {/* 后台管理员管理 */}
                <Route path="/admins/list" element={<AdminList />} />
                
                {/* 菜单管理 */}
                <Route path="/system/menus" element={<Menus />} />
                
                {/* 公告管理 */}
                <Route path="/notices" element={<NoticeList />} />
                <Route path="/notices/create" element={<NoticeForm />} />
                <Route path="/notices/edit/:id" element={<NoticeForm />} />
                
                {/* 资讯管理 */}
                <Route path="/news" element={<NewsList />} />
                <Route path="/news/create" element={<NewsForm />} />
                <Route path="/news/edit/:id" element={<NewsForm />} />

                {/* 帮助手册管理 */}
                <Route path="/help-manuals" element={<HelpManualList />} />
                <Route path="/help-manuals/create" element={<HelpManualForm />} />
                <Route path="/help-manuals/edit/:id" element={<HelpManualForm />} />

                {/* 弹窗管理 */}
                <Route path="/popups" element={<PopupList />} />
                <Route path="/popups/create" element={<PopupForm />} />
                <Route path="/popups/edit/:id" element={<PopupForm />} />

                {/* 系统设置 */}
                <Route path="/settings/regions" element={<RegionManagement />} />
                <Route path="/settings/system-config" element={<SystemConfig />} />
                <Route path="/settings/email-config" element={<EmailConfig />} />
                <Route path="/settings/email-test" element={<EmailTest />} />
                <Route path="/settings/gmail-config" element={<GmailConfig />} />
                <Route path="/settings/customer-service" element={<CustomerServiceConfig />} />
                
                {/* 个人资料 */}
                <Route path="/profile" element={<Profile />} />
                
                {/* 定时任务管理 */}
                <Route path="/cron/task-logs" element={<CronTaskLogs />} />
                
                {/* 第三方平台管理 */}
                <Route path="/third-party/apps" element={<ThirdPartyApps />} />
                <Route path="/third-party/orders" element={<ThirdPartyOrders />} />
                <Route path="/third-party/settlements" element={<ThirdPartySettlements />} />
                <Route path="/third-party/withdrawals" element={<ThirdPartyWithdrawals />} />
                
                {/* 404页面 */}
                <Route path="*" element={<div>页面不存在</div>} />
              </Routes>
            </AppLayout>
            </WebSocketProvider>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
